function extractBalancedObject(text, startPos) {
  if (startPos < 0 || startPos >= text.length || text[startPos] !== '{') return null;
  let braceCount = 0;
  let inString = false;
  let escapeNext = false;
  for (let i = startPos; i < text.length; i++) {
    const char = text[i];
    if (escapeNext) { escapeNext = false; continue; }
    if (char === '\\') { escapeNext = true; continue; }
    if (char === '"') { inString = !inString; continue; }
    if (!inString) {
      if (char === '{') braceCount++;
      if (char === '}') {
        braceCount--;
        if (braceCount === 0) {
          return { start: startPos, end: i + 1, json: text.slice(startPos, i + 1) };
        }
      }
    }
  }
  return { start: startPos, end: text.length, json: null, incomplete: true };
}

function findBoardJsonSpan(rawText) {
  const text = String(rawText || '');
  const tagged = text.match(/<ANSWER_BOARD>([\s\S]*?)(<\/ANSWER_BOARD>|$)/i);
  if (tagged) {
    const inner = tagged[1].trim();
    const brace = inner.indexOf('{');
    if (brace >= 0) {
      const span = extractBalancedObject(inner, brace);
      if (span?.json) {
        return {
          start: tagged.index,
          end: tagged.index + tagged[0].length,
          json: span.json,
        };
      }
      // incomplete tagged board — hide whole open tag through end
      return {
        start: tagged.index,
        end: text.length,
        json: null,
        incomplete: true,
        partial: inner,
      };
    }
  }

  const headlineIdx = text.search(/"headline"\s*:/);
  if (headlineIdx < 0) return null;
  let start = text.lastIndexOf('{', headlineIdx);
  if (start < 0) start = headlineIdx;
  // walk back past whitespace / markdown fences
  while (start > 0 && /[\s`]/.test(text[start - 1])) start -= 1;
  const open = text.lastIndexOf('{', headlineIdx);
  if (open < 0) {
    return { start: headlineIdx, end: text.length, json: null, incomplete: true, partial: text.slice(headlineIdx) };
  }
  const span = extractBalancedObject(text, open);
  if (!span) return null;
  if (span.json) {
    try {
      const obj = JSON.parse(span.json);
      if (obj && (obj.headline || obj.kpis || obj.people || obj.verdict)) {
        return { start: open, end: span.end, json: span.json };
      }
    } catch (_) { /* fall through */ }
  }
  return {
    start: open,
    end: text.length,
    json: null,
    incomplete: true,
    partial: text.slice(open),
  };
}

function extractPartialHeadline(rawText) {
  const m = String(rawText || '').match(/"headline"\s*:\s*"((?:\\.|[^"\\])*)"/);
  if (!m) return '';
  try {
    return JSON.parse(`"${m[1]}"`);
  } catch (_) {
    return m[1];
  }
}

function extractPartialPeriod(rawText) {
  const m = String(rawText || '').match(/"period"\s*:\s*"((?:\\.|[^"\\])*)"/);
  if (!m) return '';
  try {
    return JSON.parse(`"${m[1]}"`);
  } catch (_) {
    return m[1];
  }
}

function extractAnswerBoard(rawText) {
  const span = findBoardJsonSpan(rawText);
  if (!span?.json) return null;
  try {
    const board = JSON.parse(span.json);
    if (!board || typeof board !== 'object') return null;
    if (!(board.headline || board.kpis || board.people || board.verdict)) return null;
    return board;
  } catch (_) {
    return null;
  }
}

function stripVizNoise(text) {
  if (!text) return '';
  let cleaned = String(text);

  // Hide board JSON (complete or streaming fragment)
  let guard = 0;
  while (guard++ < 5) {
    const span = findBoardJsonSpan(cleaned);
    if (!span) break;
    cleaned = `${cleaned.slice(0, span.start)} ${cleaned.slice(span.end)}`;
  }

  cleaned = cleaned.replace(/```(?:json)?[\s\S]*?```/gi, ' ');
  cleaned = cleaned.replace(/<ANSWER_BOARD>[\s\S]*?(<\/ANSWER_BOARD>|$)/gi, ' ');
  cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, ' ');

  let maxPasses = 5;
  for (let pass = 0; pass < maxPasses; pass++) {
    const before = cleaned.length;
    const jsonPattern = /\{\s*"type"\s*:\s*"(line|bar|pie|table)"/gi;
    const match = jsonPattern.exec(cleaned);
    if (!match) break;
    const startPos = match.index;
    const span = extractBalancedObject(cleaned, startPos);
    if (span?.json) {
      cleaned = cleaned.slice(0, span.start) + ' ' + cleaned.slice(span.end);
    } else if (span?.incomplete) {
      cleaned = cleaned.slice(0, span.start);
      break;
    } else break;
    if (cleaned.length === before) break;
  }

  // Never leave raw JSON-looking leftovers as "prose"
  cleaned = cleaned.replace(/^\s*\{[\s\S]*$/g, '').trim();
  if (/^\s*["{[]/.test(cleaned) || /"headline"\s*:/.test(cleaned)) cleaned = '';

  return cleaned
    .replace(/^###?\s+/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\s{3,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function rowNameValue(row) {
  if (!row || typeof row !== 'object') return null;
  const keys = Object.keys(row);
  const nameKey = keys.find((k) => /^(name|label|user|person|member|project|category)$/i.test(k))
    || keys.find((k) => typeof row[k] === 'string');
  const valueKey = keys.find((k) => /^(hours|value|count|minutes|total|amount|score)$/i.test(k))
    || keys.find((k) => typeof row[k] === 'number');
  if (!nameKey || valueKey == null) return null;
  const name = String(row[nameKey] || '').trim();
  const value = Number(row[valueKey]);
  if (!name || !Number.isFinite(value)) return null;
  return { name, value, unit: /hour/i.test(valueKey) ? 'h' : (/min/i.test(valueKey) ? 'm' : '') };
}

function peopleFromVisualizations(visualizations = []) {
  const people = [];
  for (const viz of visualizations) {
    const chartType = viz?.chartType || viz?.type;
    if (!['bar', 'pie', 'table'].includes(chartType)) continue;
    const rows = Array.isArray(viz.data) ? viz.data : [];
    for (const row of rows) {
      const nv = rowNameValue(row);
      if (!nv) continue;
      people.push({
        name: nv.name,
        metric: `${nv.value}${nv.unit || ''}`,
        value: nv.value,
        what: '',
        signal: '',
      });
    }
  }
  const byName = new Map();
  for (const p of people) {
    const prev = byName.get(p.name.toLowerCase());
    if (!prev || p.value > prev.value) byName.set(p.name.toLowerCase(), p);
  }
  const list = [...byName.values()].sort((a, b) => b.value - a.value);
  if (!list.length) return { people: [], kpis: [] };
  const total = list.reduce((s, p) => s + p.value, 0);
  const top = list[0];
  const median = list[Math.floor(list.length / 2)]?.value || 0;
  const heavy = list.filter((p) => p.value >= median * 1.15);
  const light = list.filter((p) => p.value < median * 0.75);
  const unit = top.metric.replace(/[\d.]+/, '') || 'h';
  const kpis = [
    { label: 'People', value: String(list.length) },
    { label: 'Total', value: `${Math.round(total * 10) / 10}${unit}` },
    { label: 'Top', value: `${top.name.split(' ')[0]} ${top.metric}` },
    {
      label: heavy.length && light.length ? 'Split' : 'Spread',
      value: heavy.length && light.length
        ? `${heavy.length} heavy / ${light.length} light`
        : `${list.length} active`,
    },
  ];
  return { people: list.slice(0, 12), kpis };
}

function extractInsightLines(text) {
  const lines = (text || '').split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const insights = [];
  for (const line of lines) {
    const body = line.replace(/^(?:[-•*]|\d+[.)]|insight:?|takeaway:?|note:?)\s*/i, '').trim();
    if (body.length < 28 || body.length > 180) continue;
    if (/[{"]/.test(body) || /"headline"/.test(body)) continue;
    if (!/[A-Z][a-z]+|[0-9]|seller|makes sense|suggest|cluster|cohort|risk|focus|ops|finance|migration/i.test(body)) continue;
    if (/based on the tracking data|here'?s the|i (can|will)|let me/i.test(body)) continue;
    insights.push(body);
    if (insights.length >= 6) break;
  }
  return insights;
}

function inferVerdict(people) {
  if (!people.length) return null;
  const vals = people.map((p) => p.value);
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const ratio = min > 0 ? max / min : max;
  if (ratio >= 2.2) return { label: 'Uneven — needs role check', tone: 'mixed', score: 6 };
  if (people.length >= 4 && ratio < 1.4) return { label: 'Coherent shared load', tone: 'positive', score: 8 };
  return { label: 'Plausible with a thin tail', tone: 'mixed', score: 7 };
}

function parseAnswerVisuals(rawText, visualizations = []) {
  const board = extractAnswerBoard(rawText);
  const text = stripVizNoise(rawText);
  const fromViz = peopleFromVisualizations(visualizations);
  const streamingBoard = !board && !!findBoardJsonSpan(rawText)?.incomplete;

  if (board) {
    const people = Array.isArray(board.people) && board.people.length
      ? board.people.map((p) => ({
          name: p.name || '',
          metric: String(p.metric || p.time || p.hours || p.value || ''),
          value: parseFloat(String(p.metric || p.time || p.hours || p.value || '').replace(/[^\d.]/g, '')) || 0,
          what: p.what || p.focus || p.surface || '',
          signal: p.signal || p.band || '',
        })).filter((p) => p.name)
      : fromViz.people;
    const maxV = Math.max(...people.map((p) => p.value), 1);
    return {
      source: 'board',
      streaming: false,
      headline: board.headline || board.title || '',
      period: board.period || '',
      verdict: board.verdict || null,
      call: board.call || board.recommendation || '',
      kpis: Array.isArray(board.kpis) ? board.kpis.filter((k) => k?.label && k?.value).slice(0, 4) : fromViz.kpis,
      people: people.slice(0, 12).map((p) => ({ ...p, pct: Math.round((p.value / maxV) * 100) })),
      insights: Array.isArray(board.insights) ? board.insights.filter(Boolean).slice(0, 8) : [],
      risks: Array.isArray(board.risks) ? board.risks.filter(Boolean).slice(0, 4) : [],
      text,
    };
  }

  const people = fromViz.people;
  const maxV = Math.max(...people.map((p) => p.value), 1);
  const partialHeadline = extractPartialHeadline(rawText);
  const insights = streamingBoard ? [] : extractInsightLines(text);

  return {
    source: streamingBoard ? 'streaming' : 'inferred',
    streaming: streamingBoard,
    headline: partialHeadline || '',
    period: extractPartialPeriod(rawText),
    verdict: streamingBoard ? null : inferVerdict(people),
    call: '',
    kpis: fromViz.kpis,
    people: people.map((p) => ({ ...p, pct: Math.round((p.value / maxV) * 100) })),
    insights,
    risks: [],
    text: streamingBoard ? '' : text,
  };
}

export {
  parseAnswerVisuals,
  stripVizNoise,
  extractAnswerBoard,
  peopleFromVisualizations,
  findBoardJsonSpan,
  extractPartialHeadline,
};
